import Big from 'big.js'
import { type Nullable, assertMultiply, byDate } from 'utils'
import { productList } from '../../../../../indices-list'
import type {
  Product,
  UserPortfolioHistoryItem,
  UserProductHistory,
} from '../../../../../services/query-v2/users/history/model'
import type { PortfolioReturns } from './interface'

export interface UserReturns {
  product: Product
  date: number
  marketCap: Big
  investedValue: Big
  balance: Big
  tokenHeld: Big
}

const transformUserHistory = (
  item: UserPortfolioHistoryItem | undefined,
): Nullable<UserReturns> => {
  if (item !== undefined) {
    assertMultiply(item.balance, item.product.basePrice)
    const balance = item.balance ?? Big(0)
    const basePrice = item.product.basePrice ?? Big(0)
    return {
      product: item.product,
      balance: item.product.marketCap,
      date: item.timestamp,
      marketCap: balance.mul(basePrice),
      tokenHeld: item.balance,
      investedValue: item.investedValue,
    }
  }
}

const extractHistory = (acc: UserReturns[], curr: UserProductHistory): Nullable<UserReturns>[] => {
  return [...acc, ...curr.history.map(transformUserHistory)]
}

export const getPortfolioReturnValues = (userData: UserProductHistory[]): PortfolioReturns => {
  const isHistory = userData.some((el) => el.history.length !== 0)
  const indicesCollection = Object.keys(productList)
  const returns: { [symbol: string]: PortfolioReturns } = {}

  const userHistories: UserReturns[] = isHistory
    ? userData
        .reduce(
          (acc, curr) =>
            extractHistory(acc, curr) !== undefined && extractHistory(acc, curr) !== null
              ? (extractHistory(acc, curr) as UserReturns[])
              : ([] as UserReturns[]),
          [] as UserReturns[],
        )
        .sort(byDate)
    : []

  // init with 0 and update as we loop over
  const lastBalanceOf: { [key: string]: { balance: Big; buyPrice: Big } } =
    indicesCollection.reduce((a, v) => ({ ...a, [v]: { balance: Big(0), buyPrice: Big(0) } }), {})

  const getLastBalance = (id: string) => {
    const balance = lastBalanceOf[id].balance
    return balance
  }

  const setLastBalanceAndPrice = (id: string, bal: Big, price: Big) => {
    lastBalanceOf[id].balance = bal
    lastBalanceOf[id].buyPrice = price
  }

  let totalCapitalization = Big(0)

  const avgPrices: { [key: string]: Big } = {
    JMX: Big(0),
    CAI: Big(0),
    ARTXAI: Big(0),
  }

  const response: PortfolioReturns = {
    investmentValue: Big(0),
    investedCapital: Big(0),
  }
  for (let i = 0; i < userHistories.length; i++) {
    const userHistory = userHistories[i] as UserReturns

    const symbol = userHistory.product.symbol
    const productId = userHistory.product.id
    const price = userHistory.product.basePrice
    const held = userHistory.tokenHeld

    if (getLastBalance(productId)) {
      if (userHistory.tokenHeld.eq(0) && avgPrices[symbol].eq(0)) {
        avgPrices[symbol] = Big(0)
      } else {
        if (userHistory.tokenHeld.eq(0)) {
          avgPrices[symbol] = Big(0)
        } else {
          const isBigger = userHistory.tokenHeld.gt(getLastBalance(productId))
          if (isBigger) {
            const diff = userHistory.tokenHeld.minus(getLastBalance(productId))

            assertMultiply(avgPrices[symbol], getLastBalance(productId))
            const oldBalance = Big(avgPrices[symbol]).mul(getLastBalance(productId))

            assertMultiply(diff, price)
            const newBalanceRest = Big(diff.mul(price))
            avgPrices[symbol] = Big(Big(oldBalance).add(newBalanceRest)).div(userHistory.tokenHeld)
          }
        }
      }

      if (Big(totalCapitalization).gt(0)) {
        totalCapitalization = Big(totalCapitalization).sub(getLastBalance(productId))
      }
      totalCapitalization = Big(totalCapitalization).add(Big(userHistory.marketCap))
    }
    setLastBalanceAndPrice(productId, held, price)

    assertMultiply(held, avgPrices[symbol])
    const payload = {
      investmentValue: userHistory.investedValue,
      investedCapital: held.mul(avgPrices[symbol]),
      symbol: symbol,
    }
    if (userHistories[i + 1] === undefined || userHistories[i + 1].product.symbol !== symbol) {
      returns[symbol] = payload
    }
  }
  for (const { investedCapital, investmentValue } of Object.values(returns)) {
    response.investedCapital = Big(response.investedCapital ?? 0).add(investedCapital ?? 0)
    response.investmentValue = Big(response.investmentValue ?? 0).add(investmentValue ?? 0)
  }
  return response
}
